import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import StatusBadge from '../../components/common/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Checkbox } from '../../components/ui/checkbox';
import { cn, formatDateTimeLocal } from '../../lib/utils';
import { Plus, ChevronLeft, ChevronRight, AlertTriangle, CalendarIcon, Clock, User, Home, Edit, Trash2, Moon, Copy, ClipboardPaste, Check, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

// House color palette for visual separation
const HOUSE_COLORS = [
  { bg: 'bg-blue-500/15', border: 'border-blue-500/40', text: 'text-blue-700 dark:text-blue-300', accent: 'bg-blue-500' },
  { bg: 'bg-emerald-500/15', border: 'border-emerald-500/40', text: 'text-emerald-700 dark:text-emerald-300', accent: 'bg-emerald-500' },
  { bg: 'bg-purple-500/15', border: 'border-purple-500/40', text: 'text-purple-700 dark:text-purple-300', accent: 'bg-purple-500' },
  { bg: 'bg-amber-500/15', border: 'border-amber-500/40', text: 'text-amber-700 dark:text-amber-300', accent: 'bg-amber-500' },
  { bg: 'bg-rose-500/15', border: 'border-rose-500/40', text: 'text-rose-700 dark:text-rose-300', accent: 'bg-rose-500' },
  { bg: 'bg-cyan-500/15', border: 'border-cyan-500/40', text: 'text-cyan-700 dark:text-cyan-300', accent: 'bg-cyan-500' },
  { bg: 'bg-orange-500/15', border: 'border-orange-500/40', text: 'text-orange-700 dark:text-orange-300', accent: 'bg-orange-500' },
  { bg: 'bg-indigo-500/15', border: 'border-indigo-500/40', text: 'text-indigo-700 dark:text-indigo-300', accent: 'bg-indigo-500' },
];

// Worker color palette - distinct colors for each worker
const WORKER_COLORS = [
  { bg: 'bg-blue-100 dark:bg-blue-900/40', border: 'border-blue-400 dark:border-blue-600', text: 'text-blue-800 dark:text-blue-200' },
  { bg: 'bg-emerald-100 dark:bg-emerald-900/40', border: 'border-emerald-400 dark:border-emerald-600', text: 'text-emerald-800 dark:text-emerald-200' },
  { bg: 'bg-purple-100 dark:bg-purple-900/40', border: 'border-purple-400 dark:border-purple-600', text: 'text-purple-800 dark:text-purple-200' },
  { bg: 'bg-amber-100 dark:bg-amber-900/40', border: 'border-amber-400 dark:border-amber-600', text: 'text-amber-800 dark:text-amber-200' },
  { bg: 'bg-rose-100 dark:bg-rose-900/40', border: 'border-rose-400 dark:border-rose-600', text: 'text-rose-800 dark:text-rose-200' },
  { bg: 'bg-cyan-100 dark:bg-cyan-900/40', border: 'border-cyan-400 dark:border-cyan-600', text: 'text-cyan-800 dark:text-cyan-200' },
  { bg: 'bg-orange-100 dark:bg-orange-900/40', border: 'border-orange-400 dark:border-orange-600', text: 'text-orange-800 dark:text-orange-200' },
  { bg: 'bg-indigo-100 dark:bg-indigo-900/40', border: 'border-indigo-400 dark:border-indigo-600', text: 'text-indigo-800 dark:text-indigo-200' },
  { bg: 'bg-pink-100 dark:bg-pink-900/40', border: 'border-pink-400 dark:border-pink-600', text: 'text-pink-800 dark:text-pink-200' },
  { bg: 'bg-teal-100 dark:bg-teal-900/40', border: 'border-teal-400 dark:border-teal-600', text: 'text-teal-800 dark:text-teal-200' },
  { bg: 'bg-lime-100 dark:bg-lime-900/40', border: 'border-lime-400 dark:border-lime-600', text: 'text-lime-800 dark:text-lime-200' },
  { bg: 'bg-sky-100 dark:bg-sky-900/40', border: 'border-sky-400 dark:border-sky-600', text: 'text-sky-800 dark:text-sky-200' },
  { bg: 'bg-violet-100 dark:bg-violet-900/40', border: 'border-violet-400 dark:border-violet-600', text: 'text-violet-800 dark:text-violet-200' },
  { bg: 'bg-fuchsia-100 dark:bg-fuchsia-900/40', border: 'border-fuchsia-400 dark:border-fuchsia-600', text: 'text-fuchsia-800 dark:text-fuchsia-200' },
  { bg: 'bg-red-100 dark:bg-red-900/40', border: 'border-red-400 dark:border-red-600', text: 'text-red-800 dark:text-red-200' },
  { bg: 'bg-green-100 dark:bg-green-900/40', border: 'border-green-400 dark:border-green-600', text: 'text-green-800 dark:text-green-200' },
];

// Shift type colors
const SHIFT_TYPE_COLORS = {
  'AM': 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-400 text-yellow-800 dark:text-yellow-200',
  'PM': 'bg-orange-100 dark:bg-orange-900/30 border-orange-400 text-orange-800 dark:text-orange-200',
  'Active Night': 'bg-indigo-100 dark:bg-indigo-900/30 border-indigo-400 text-indigo-800 dark:text-indigo-200',
  'Sleepover': 'bg-purple-100 dark:bg-purple-900/30 border-purple-400 text-purple-800 dark:text-purple-200',
};

// Format time for display (extracts HH:MM from datetime string)
// Adelaide timezone constant for proper date extraction
const ADELAIDE_TIMEZONE = 'Australia/Adelaide';

// Format time for display - MUST convert to Adelaide timezone
const formatTimeDisplay = (dateTimeStr) => {
  if (!dateTimeStr) return '';
  
  // Parse the datetime and convert to Adelaide timezone
  const date = new Date(dateTimeStr);
  
  // Format time in Adelaide timezone
  return date.toLocaleTimeString('en-AU', {
    timeZone: ADELAIDE_TIMEZONE,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).toLowerCase().replace(' ', '');
};

// Extract just the date part YYYY-MM-DD from a datetime string or Date
// CRITICAL: Must convert UTC times to Adelaide timezone before extracting date
const extractDateString = (input) => {
  if (!input) return '';
  
  if (typeof input === 'string') {
    // Check if it's an ISO string with timezone (e.g., "2026-01-20T00:22:02.897948+00:00" or "2026-01-20T06:00:00Z")
    if (input.includes('Z') || input.includes('+') || /T\d{2}:\d{2}:\d{2}[+-]/.test(input)) {
      // Parse and convert to Adelaide timezone
      const date = new Date(input);
      return date.toLocaleDateString('en-CA', { timeZone: ADELAIDE_TIMEZONE });
    }
    
    // For local datetime strings like "2026-01-19T06:00" (no timezone), assume Adelaide time
    const match = input.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : '';
  }
  
  // For Date objects, convert to Adelaide timezone
  if (input instanceof Date) {
    return input.toLocaleDateString('en-CA', { timeZone: ADELAIDE_TIMEZONE });
  }
  
  return '';
};

// Get the Monday of the week containing the given date (in Adelaide timezone)
const getMondayOfWeek = (date) => {
  // Get date string in Adelaide timezone
  const adelaideDateStr = date.toLocaleDateString('en-CA', { timeZone: ADELAIDE_TIMEZONE });
  const [year, month, day] = adelaideDateStr.split('-').map(Number);
  
  // Create date at noon to avoid DST issues
  const d = new Date(year, month - 1, day, 12, 0, 0);
  const dayOfWeek = d.getDay(); // 0 = Sunday, 1 = Monday, etc.
  
  // Calculate days to subtract to get to Monday
  // If Sunday (0), go back 6 days. If Monday (1), go back 0 days. etc.
  const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  
  d.setDate(d.getDate() - daysToSubtract);
  return d;
};

// Generate date range for calendar - returns array of date strings
// STATIC: Always starts on Monday and shows 14 days (Mon-Sun fortnight)
const generateCalendarDates = (referenceDate, days = 14) => {
  const dates = [];
  
  // Get the Monday of the week containing the reference date
  const monday = getMondayOfWeek(referenceDate);
  const startYear = monday.getFullYear();
  const startMonth = monday.getMonth();
  const startDay = monday.getDate();
  
  for (let i = 0; i < days; i++) {
    // Calculate the date by adding days from Monday
    const tempDate = new Date(startYear, startMonth, startDay + i, 12, 0, 0); // noon to avoid DST issues
    const dateStr = `${tempDate.getFullYear()}-${String(tempDate.getMonth() + 1).padStart(2, '0')}-${String(tempDate.getDate()).padStart(2, '0')}`;
    
    dates.push({
      dateStr: dateStr,
      date: tempDate,
      dayName: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][tempDate.getDay()],
      dayNum: tempDate.getDate(),
      monthDay: `${String(tempDate.getMonth() + 1).padStart(2, '0')}-${String(tempDate.getDate()).padStart(2, '0')}`
    });
  }
  return dates;
};

// Check if date string is today
const isTodayStr = (dateStr) => {
  const today = new Date();
  return extractDateString(today) === dateStr;
};

// Check if shift is overnight
const isOvernightShift = (shift) => {
  if (!shift.start_time || !shift.end_time) return false;
  const type = shift.shift_type?.toLowerCase() || '';
  if (type.includes('night') || type.includes('sleepover')) return true;
  const startDate = extractDateString(shift.start_time);
  const endDate = extractDateString(shift.end_time);
  return startDate !== endDate;
};

const RosterPage = () => {
  const { api, isAdmin, isHouseManager } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [houses, setHouses] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filter state
  const [selectedHouse, setSelectedHouse] = useState('all');
  const [selectedWorker, setSelectedWorker] = useState('all');
  const [selectedShiftType, setSelectedShiftType] = useState('all');
  const [startDate, setStartDate] = useState(new Date());
  const [showFilters, setShowFilters] = useState(false);
  
  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedShift, setEditedShift] = useState(null);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  
  // Copy/Paste state
  const [clipboard, setClipboard] = useState([]);
  const [selectedShifts, setSelectedShifts] = useState(new Set());
  const [pasteDialogOpen, setPasteDialogOpen] = useState(false);
  const [pasteLoading, setPasteLoading] = useState(false);
  const [copyMode, setCopyMode] = useState('single'); // single, bulk, weekly, fortnightly
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [copyFilterHouse, setCopyFilterHouse] = useState('all');
  const [pasteOptions, setPasteOptions] = useState({
    target_date: '',
    house_id: '',
    worker_id: '',
    override_reason: ''
  });

  const canEdit = isAdmin || isHouseManager;
  const canSeeShiftReport = isAdmin;

  // Generate calendar dates array
  const calendarDates = useMemo(() => generateCalendarDates(startDate, 14), [startDate]);

  // House color mapping
  const houseColorMap = useMemo(() => {
    const map = {};
    houses.forEach((house, idx) => {
      map[house.id] = HOUSE_COLORS[idx % HOUSE_COLORS.length];
    });
    return map;
  }, [houses]);

  // Worker color mapping - assigns a consistent color to each worker
  const workerColorMap = useMemo(() => {
    const map = {};
    workers.forEach((worker, idx) => {
      map[worker.id] = WORKER_COLORS[idx % WORKER_COLORS.length];
    });
    return map;
  }, [workers]);

  // Get worker color by ID (with fallback for unassigned)
  const getWorkerColor = useCallback((workerId) => {
    if (!workerId) {
      // Unassigned/Open shift - gray color
      return { bg: 'bg-gray-100 dark:bg-gray-800', border: 'border-gray-400 dark:border-gray-600', text: 'text-gray-700 dark:text-gray-300' };
    }
    return workerColorMap[workerId] || WORKER_COLORS[0];
  }, [workerColorMap]);

  // Get default shift times
  const getDefaultTimes = useCallback((shiftType = 'AM') => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    let startHour, endHour;
    switch (shiftType) {
      case 'AM': startHour = 6; endHour = 14; break;
      case 'PM': startHour = 14; endHour = 22; break;
      case 'Active Night': startHour = 22; endHour = 6; break;
      case 'Sleepover': startHour = 21; endHour = 7; break;
      default: startHour = 9; endHour = 17;
    }
    
    const startDate = new Date(tomorrow);
    startDate.setHours(startHour, 0, 0, 0);
    
    const endDate = new Date(tomorrow);
    if (endHour < startHour) endDate.setDate(endDate.getDate() + 1);
    endDate.setHours(endHour, 0, 0, 0);
    
    return { startDate, endDate };
  }, []);

  // New shift state
  const [newShift, setNewShift] = useState(() => {
    const { startDate, endDate } = getDefaultTimes('AM');
    return {
      house_id: '',
      worker_id: '',
      shift_type: 'AM',
      start_time: formatDateTimeLocal(startDate),
      end_time: formatDateTimeLocal(endDate),
      participant_ids: [],
      notes: '',
      status: 'offered'
    };
  });

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const calDates = generateCalendarDates(startDate, 14);
      const startStr = calDates[0].dateStr;
      const endStr = calDates[calDates.length - 1].dateStr;

      // Fetch shifts, houses, and participants - these work for all roles
      const [shiftsRes, housesRes, participantsRes] = await Promise.all([
        api.get('/shifts', { params: { start_date: startStr, end_date: endStr } }),
        api.get('/houses'),
        api.get('/participants')
      ]);

      setShifts(shiftsRes.data);
      setHouses(housesRes.data);
      setParticipants(participantsRes.data);

      // Try to fetch workers list - this may fail for non-admin users
      // Workers don't need this data for viewing their own roster
      try {
        const workersRes = await api.get('/users', { params: { role: 'worker,house_manager' } });
        setWorkers(workersRes.data);
      } catch (workerErr) {
        // Silently handle - workers don't have permission to list all workers
        // They can still view shifts which already include worker_name
        setWorkers([]);
      }
    } catch (error) {
      toast.error('Failed to load roster data');
    } finally {
      setLoading(false);
    }
  }, [api, startDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get shifts for a specific date string and house
  const getShiftsForDateAndHouse = useCallback((dateStr, houseId) => {
    return shifts.filter(s => {
      // Extract date from shift start_time - converts UTC to Adelaide timezone
      const shiftDateStr = extractDateString(s.start_time);
      const matchesDate = shiftDateStr === dateStr;
      const matchesHouse = selectedHouse === 'all' ? s.house_id === houseId : s.house_id === houseId;
      const matchesWorker = selectedWorker === 'all' || s.worker_id === selectedWorker;
      const matchesType = selectedShiftType === 'all' || s.shift_type === selectedShiftType;
      return matchesDate && matchesHouse && matchesWorker && matchesType;
    });
  }, [shifts, selectedHouse, selectedWorker, selectedShiftType]);

  // Navigation
  const navigateWeek = (direction) => {
    const newDate = new Date(startDate);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setStartDate(newDate);
  };

  const goToToday = () => setStartDate(new Date());

  // Shift actions
  const handleCreateShift = async () => {
    if (!newShift.house_id || !newShift.start_time || !newShift.end_time) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      await api.post('/shifts', newShift);
      toast.success('Shift created successfully');
      setCreateDialogOpen(false);
      const { startDate: defaultStart, endDate: defaultEnd } = getDefaultTimes('AM');
      setNewShift({
        house_id: '',
        worker_id: '',
        shift_type: 'AM',
        start_time: formatDateTimeLocal(defaultStart),
        end_time: formatDateTimeLocal(defaultEnd),
        participant_ids: [],
        notes: '',
        status: 'offered'
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create shift');
    }
  };

  const handleShiftClick = (shift) => {
    setSelectedShift(shift);
    setEditedShift({ ...shift });
    setShiftDialogOpen(true);
    setIsEditing(false);
  };

  const handleUpdateShift = async () => {
    if (!editedShift) return;
    try {
      await api.put(`/shifts/${editedShift.id}`, {
        house_id: editedShift.house_id,
        worker_id: editedShift.worker_id || null,
        shift_type: editedShift.shift_type,
        start_time: editedShift.start_time,
        end_time: editedShift.end_time,
        participant_ids: editedShift.participant_ids || [],
        notes: editedShift.notes,
        status: editedShift.status
      });
      toast.success('Shift updated successfully');
      setShiftDialogOpen(false);
      setIsEditing(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update shift');
    }
  };

  const handleDeleteShift = async (shiftId) => {
    try {
      await api.delete(`/shifts/${shiftId}`);
      toast.success('Shift deleted');
      setShiftDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete shift');
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(Array.from(selectedShifts).map(id => api.delete(`/shifts/${id}`)));
      toast.success(`Deleted ${selectedShifts.size} shifts`);
      setSelectedShifts(new Set());
      setBulkDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete some shifts');
    }
  };

  // Selection handlers
  const toggleShiftSelection = (shiftId) => {
    const newSelection = new Set(selectedShifts);
    if (newSelection.has(shiftId)) {
      newSelection.delete(shiftId);
    } else {
      newSelection.add(shiftId);
    }
    setSelectedShifts(newSelection);
  };

  const clearSelection = () => setSelectedShifts(new Set());

  // Copy/Paste handlers
  const handleCopyShifts = () => {
    setCopyFilterHouse('all');
    setCopyDialogOpen(true);
  };

  const executeCopy = () => {
    let shiftsToCopy = [];
    
    if (copyMode === 'single' || copyMode === 'bulk') {
      // Copy selected shifts
      shiftsToCopy = shifts.filter(s => selectedShifts.has(s.id));
    } else if (copyMode === 'weekly') {
      // Copy all shifts from the first 7 days of current view
      const weekDates = calendarDates.slice(0, 7).map(d => d.dateStr);
      shiftsToCopy = shifts.filter(s => {
        const shiftDate = extractDateString(s.start_time);
        if (!weekDates.includes(shiftDate)) return false;
        if (copyFilterHouse !== 'all' && s.house_id !== copyFilterHouse) return false;
        return true;
      });
    } else if (copyMode === 'fortnightly') {
      // Copy all shifts from the current 14-day view
      const fortnightDates = calendarDates.map(d => d.dateStr);
      shiftsToCopy = shifts.filter(s => {
        const shiftDate = extractDateString(s.start_time);
        if (!fortnightDates.includes(shiftDate)) return false;
        if (copyFilterHouse !== 'all' && s.house_id !== copyFilterHouse) return false;
        return true;
      });
    }
    
    if (shiftsToCopy.length === 0) {
      toast.error('No shifts to copy');
      return;
    }
    
    // Store with relative day offset for proper pasting
    const baseDate = copyMode === 'weekly' || copyMode === 'fortnightly' 
      ? calendarDates[0].dateStr 
      : extractDateString(shiftsToCopy[0]?.start_time);
    
    const shiftsWithOffset = shiftsToCopy.map(shift => {
      const shiftDate = extractDateString(shift.start_time);
      const dayOffset = Math.floor((new Date(shiftDate) - new Date(baseDate)) / (1000 * 60 * 60 * 24));
      return { ...shift, _dayOffset: dayOffset, _copyMode: copyMode };
    });
    
    setClipboard(shiftsWithOffset);
    setCopyDialogOpen(false);
    toast.success(`Copied ${shiftsToCopy.length} shift(s) to clipboard (${copyMode} mode)`);
  };

  const handlePasteShifts = async () => {
    if (!pasteOptions.target_date) {
      toast.error('Please select a target date');
      return;
    }
    setPasteLoading(true);
    try {
      const targetBaseDate = new Date(pasteOptions.target_date);
      let createdCount = 0;
      
      for (const shift of clipboard) {
        // Calculate the actual target date based on day offset
        const targetDate = new Date(targetBaseDate);
        targetDate.setDate(targetDate.getDate() + (shift._dayOffset || 0));
        const targetDateStr = `${targetDate.getFullYear()}-${String(targetDate.getMonth() + 1).padStart(2, '0')}-${String(targetDate.getDate()).padStart(2, '0')}`;
        
        // Handle overnight shifts where end_time is next day
        const startTime = shift.start_time.split('T')[1] || '09:00';
        let endTime = shift.end_time.split('T')[1] || '17:00';
        let endDateStr = targetDateStr;
        
        // Check if original shift spans midnight
        const originalStartDate = extractDateString(shift.start_time);
        const originalEndDate = extractDateString(shift.end_time);
        if (originalEndDate > originalStartDate) {
          // Shift spans to next day
          const endDate = new Date(targetDate);
          endDate.setDate(endDate.getDate() + 1);
          endDateStr = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
        }
        
        const newShiftData = {
          house_id: shift.house_id,
          worker_id: shift.worker_id,
          shift_type: shift.shift_type,
          start_time: `${targetDateStr}T${startTime}`,
          end_time: `${endDateStr}T${endTime}`,
          participant_ids: shift.participant_ids || [],
          notes: shift.notes || '',
          status: 'offered'
        };
        
        await api.post('/shifts', newShiftData);
        createdCount++;
      }
      
      toast.success(`Created ${createdCount} shift(s)`);
      setPasteDialogOpen(false);
      setPasteOptions({ target_date: '', house_id: '', worker_id: '', override_reason: '' });
      fetchData();
    } catch (error) {
      console.error('Paste error:', error);
      toast.error('Failed to paste shifts');
    } finally {
      setPasteLoading(false);
    }
  };

  // Always show 14 days (fortnight)
  const visibleDates = calendarDates;

  // Get filtered houses for display
  const displayHouses = useMemo(() => {
    if (selectedHouse === 'all') return houses;
    return houses.filter(h => h.id === selectedHouse);
  }, [houses, selectedHouse]);

  // Auto-detect shift type based on start and end times
  // Aligned with SCHADS Award (Social, Community, Home Care and Disability Services Award)
  // Rules:
  // - Day (AM): Shifts ending before 8:00 PM (20:00) - standard hours
  // - Evening (PM): Shifts that include work between 8:00 PM and midnight
  // - Active Night: Shifts that include work between midnight and 6:00 AM
  // - Sleepover: Overnight shifts typically 10pm-6am with flat allowance
  const detectShiftType = useCallback((startTime, endTime) => {
    if (!startTime || !endTime) return null;
    
    // Parse times
    const startDate = new Date(startTime);
    const endDate = new Date(endTime);
    
    const startHour = startDate.getHours();
    const endHour = endDate.getHours();
    
    // Check if shift spans overnight (different dates)
    const startDateStr = startTime.split('T')[0];
    const endDateStr = endTime.split('T')[0];
    const isOvernight = startDateStr !== endDateStr;
    
    // Calculate duration in hours
    const durationMs = endDate - startDate;
    const durationHours = durationMs / (1000 * 60 * 60);
    
    // Sleepover: Overnight shift starting around 10pm or later, typically 8+ hours
    // SCHADS: Usually 10pm-6am with flat allowance
    if (isOvernight && startHour >= 21 && durationHours >= 7 && durationHours <= 12) {
      // If ending at or after 7am, likely a sleepover (longer rest period)
      if (endHour >= 7) {
        return 'Sleepover';
      }
      // Active Night: overnight shift ending around 6am (midnight-6am work)
      return 'Active Night';
    }
    
    // Active Night: Any shift that includes midnight to 6am hours
    // Typically starts late evening and ends early morning
    if (isOvernight && (startHour >= 20 || endHour <= 6)) {
      return 'Active Night';
    }
    
    // Day shift: Ends before 8:00 PM (20:00) - standard SCHADS hours
    if (!isOvernight && endHour < 20) {
      return 'AM';
    }
    
    // Evening/Afternoon shift: Ends at or after 8:00 PM but before midnight
    // SCHADS: 8pm-midnight attracts evening penalty (112.5%)
    if (!isOvernight && endHour >= 20) {
      return 'PM';
    }
    
    return null; // Can't determine, don't auto-change
  }, []);

  // Handle time changes and auto-detect shift type
  const handleStartTimeChange = (value) => {
    setNewShift(prev => {
      const newState = { ...prev, start_time: value };
      // Auto-detect shift type if we have both times
      if (value && prev.end_time) {
        const detectedType = detectShiftType(value, prev.end_time);
        if (detectedType) {
          newState.shift_type = detectedType;
        }
      }
      return newState;
    });
  };

  const handleEndTimeChange = (value) => {
    setNewShift(prev => {
      const newState = { ...prev, end_time: value };
      // Auto-detect shift type if we have both times
      if (prev.start_time && value) {
        const detectedType = detectShiftType(prev.start_time, value);
        if (detectedType) {
          newState.shift_type = detectedType;
        }
      }
      return newState;
    });
  };

  // Shift type change handler - PRESERVES the date, only changes time based on shift type
  const handleShiftTypeChange = (type) => {
    setNewShift(prev => {
      // Extract the current date from the start_time (preserve user's selected date)
      const currentDateStr = prev.start_time ? prev.start_time.split('T')[0] : '';
      
      if (!currentDateStr) {
        // Fallback to default behavior if no date is set
        const { startDate, endDate } = getDefaultTimes(type);
        return {
          ...prev,
          shift_type: type,
          start_time: formatDateTimeLocal(startDate),
          end_time: formatDateTimeLocal(endDate)
        };
      }
      
      // Determine start and end hours based on shift type
      let startHour, startMin, endHour, endMin;
      let endDateStr = currentDateStr; // Same day by default
      
      switch (type) {
        case 'AM':
          startHour = '06'; startMin = '00';
          endHour = '14'; endMin = '00';
          break;
        case 'PM':
          startHour = '14'; startMin = '00';
          endHour = '22'; endMin = '00';
          break;
        case 'Active Night':
          startHour = '22'; startMin = '00';
          endHour = '06'; endMin = '00';
          // End time is next day
          const nextDay = new Date(currentDateStr);
          nextDay.setDate(nextDay.getDate() + 1);
          endDateStr = `${nextDay.getFullYear()}-${String(nextDay.getMonth() + 1).padStart(2, '0')}-${String(nextDay.getDate()).padStart(2, '0')}`;
          break;
        case 'Sleepover':
          startHour = '21'; startMin = '00';
          endHour = '07'; endMin = '00';
          // End time is next day
          const sleepoverNext = new Date(currentDateStr);
          sleepoverNext.setDate(sleepoverNext.getDate() + 1);
          endDateStr = `${sleepoverNext.getFullYear()}-${String(sleepoverNext.getMonth() + 1).padStart(2, '0')}-${String(sleepoverNext.getDate()).padStart(2, '0')}`;
          break;
        default:
          startHour = '09'; startMin = '00';
          endHour = '17'; endMin = '00';
      }
      
      return {
        ...prev,
        shift_type: type,
        start_time: `${currentDateStr}T${startHour}:${startMin}`,
        end_time: `${endDateStr}T${endHour}:${endMin}`
      };
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in" data-testid="roster-page">
      <PageHeader 
        title="Roster Management"
        description="2-week roster view with visual house separation"
        actions={
          <div className="flex gap-2 flex-wrap">
            {canEdit && (
              <Button variant="outline" onClick={handleCopyShifts} data-testid="copy-shifts-btn">
                <Copy className="h-4 w-4 mr-2" />
                Copy {selectedShifts.size > 0 ? `(${selectedShifts.size})` : 'Week/Fortnight'}
              </Button>
            )}
            {canEdit && selectedShifts.size > 0 && (
              <>
                <Button onClick={handlePublishShifts} className="bg-green-600 hover:bg-green-700">
                  <Send className="h-4 w-4 mr-2" />
                  Publish ({selectedShifts.size})
                </Button>
                <Button variant="destructive" onClick={() => setBulkDeleteDialogOpen(true)}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete ({selectedShifts.size})
                </Button>
              </>
            )}
            {canEdit && clipboard.length > 0 && (
              <Button variant="outline" onClick={() => setPasteDialogOpen(true)} className="border-primary/50">
                <ClipboardPaste className="h-4 w-4 mr-2" />
                Paste ({clipboard.length})
              </Button>
            )}
            {selectedShifts.size > 0 && (
              <Button variant="ghost" size="sm" onClick={clearSelection}>Clear</Button>
            )}
            {canEdit && (
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="create-shift-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    New Shift
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create New Shift</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>House *</Label>
                      <Select value={newShift.house_id} onValueChange={(v) => setNewShift({...newShift, house_id: v})}>
                        <SelectTrigger><SelectValue placeholder="Select house" /></SelectTrigger>
                        <SelectContent>
                          {houses.map(h => (
                            <SelectItem key={h.id} value={h.id}>
                              <div className="flex items-center gap-2">
                                <div className={cn("w-3 h-3 rounded-full", houseColorMap[h.id]?.accent)} />
                                {h.name}
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Shift Type</Label>
                      <Select value={newShift.shift_type} onValueChange={handleShiftTypeChange}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="AM">AM (Day)</SelectItem>
                          <SelectItem value="PM">PM (Evening)</SelectItem>
                          <SelectItem value="Active Night">Active Night</SelectItem>
                          <SelectItem value="Sleepover">Sleepover</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Start Time *</Label>
                        <Input type="datetime-local" value={newShift.start_time} onChange={(e) => handleStartTimeChange(e.target.value)} />
                      </div>
                      <div className="space-y-2">
                        <Label>End Time *</Label>
                        <Input type="datetime-local" value={newShift.end_time} onChange={(e) => handleEndTimeChange(e.target.value)} />
                      </div>
                    </div>
                    {/* Auto-detection hint - SCHADS Award aligned */}
                    <p className="text-xs text-muted-foreground -mt-2">
                      Auto-detects per SCHADS: Day (ends before 8pm), PM (ends 8pm-midnight), Overnight (10pm-6am)
                    </p>
                    <div className="space-y-2">
                      <Label>Assign Worker</Label>
                      <Select value={newShift.worker_id || 'unassigned'} onValueChange={(v) => setNewShift({...newShift, worker_id: v === 'unassigned' ? '' : v})}>
                        <SelectTrigger><SelectValue placeholder="Select worker" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Unassigned (Open Shift)</SelectItem>
                          {workers.map(w => (
                            <SelectItem key={w.id} value={w.id}>{w.first_name} {w.last_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Notes</Label>
                      <Textarea value={newShift.notes} onChange={(e) => setNewShift({...newShift, notes: e.target.value})} placeholder="Optional notes..." rows={2} />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleCreateShift}>Create Shift</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
        }
      />

      {/* Filters Bar */}
      <Card className="border-border/40">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Date Navigation */}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => navigateWeek(-1)}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="min-w-[200px]">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    {calendarDates[0]?.dateStr} to {calendarDates[calendarDates.length - 1]?.dateStr}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={startDate} onSelect={(date) => { if (date) { setStartDate(date); setCalendarOpen(false); }}} />
                </PopoverContent>
              </Popover>
              <Button variant="outline" size="icon" onClick={() => navigateWeek(1)}>
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="sm" onClick={goToToday}>Today</Button>
            </div>

            <div className="h-6 w-px bg-border hidden md:block" />

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Home className="h-4 w-4 text-muted-foreground" />
                <Select value={selectedHouse} onValueChange={setSelectedHouse}>
                  <SelectTrigger className="w-[160px]"><SelectValue placeholder="All Houses" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Houses</SelectItem>
                    {houses.map(h => (
                      <SelectItem key={h.id} value={h.id}>
                        <div className="flex items-center gap-2">
                          <div className={cn("w-3 h-3 rounded-full", houseColorMap[h.id]?.accent)} />
                          {h.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <Select value={selectedWorker} onValueChange={setSelectedWorker}>
                  <SelectTrigger className="w-[160px]"><SelectValue placeholder="All Workers" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Workers</SelectItem>
                    {workers.map(w => (
                      <SelectItem key={w.id} value={w.id}>{w.first_name} {w.last_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <Select value={selectedShiftType} onValueChange={setSelectedShiftType}>
                  <SelectTrigger className="w-[140px]"><SelectValue placeholder="All Types" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="AM">AM (Day)</SelectItem>
                    <SelectItem value="PM">PM (Evening)</SelectItem>
                    <SelectItem value="Active Night">Active Night</SelectItem>
                    <SelectItem value="Sleepover">Sleepover</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* House Color Legend */}
      <div className="flex flex-wrap items-center gap-3 px-2">
        <span className="text-sm text-muted-foreground font-medium">Houses:</span>
        {houses.map(house => (
          <div key={house.id} className="flex items-center gap-1.5">
            <div className={cn("w-3 h-3 rounded-full", houseColorMap[house.id]?.accent)} />
            <span className="text-sm">{house.name}</span>
          </div>
        ))}
      </div>

      {/* Roster Grid - Always shows 14 days (Mon-Sun fortnight) */}
      <Card className="border-border/40 overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <div className="min-w-[1400px]">
              {/* Header Row */}
              <div className="grid grid-cols-[120px_repeat(14,minmax(85px,1fr))] border-b-2 border-border bg-muted/30 sticky top-0 z-10">
                <div className="p-3 font-semibold text-sm text-muted-foreground flex items-center gap-2 border-r-2 border-border">
                  <MapPin className="h-4 w-4" />
                  House
                </div>
                {visibleDates.map((dateInfo, idx) => (
                  <div key={idx} className={cn(
                    "p-2 text-center border-r border-border/40", 
                    isTodayStr(dateInfo.dateStr) && "bg-primary/10"
                  )}>
                    <div className="text-xs text-muted-foreground font-medium">{dateInfo.dayName}</div>
                    <div className={cn("text-lg font-bold", isTodayStr(dateInfo.dateStr) && "text-primary")}>
                      {dateInfo.dayNum}
                    </div>
                  </div>
                ))}
              </div>

              {/* House Rows */}
              {displayHouses.map((house, houseIdx) => {
                const houseColor = houseColorMap[house.id] || HOUSE_COLORS[0];
                return (
                  <div key={house.id} className={cn(
                    "grid grid-cols-[120px_repeat(14,minmax(85px,1fr))] border-b-2",
                    houseColor.border
                  )}>
                    {/* House Label */}
                    <div className={cn("p-3 font-medium text-sm flex flex-col gap-1 border-r-2 border-border", houseColor.bg)}>
                      <div className="flex items-center gap-2">
                        <div className={cn("w-3 h-3 rounded-full flex-shrink-0", houseColor.accent)} />
                        <span className={cn("font-semibold truncate", houseColor.text)}>{house.name}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">Ratio: {house.staffing_ratio || '1:3'}</span>
                    </div>

                    {/* Day Cells */}
                    {visibleDates.map((dateInfo, idx) => {
                      const dayShifts = getShiftsForDateAndHouse(dateInfo.dateStr, house.id);
                      const sortedShifts = [...dayShifts].sort((a, b) => {
                        const aTime = a.start_time?.split('T')[1] || '00:00';
                        const bTime = b.start_time?.split('T')[1] || '00:00';
                        return aTime.localeCompare(bTime);
                      });

                      return (
                        <div 
                          key={idx} 
                          className={cn(
                            "p-1 min-h-[100px] border-r border-border/40 space-y-1", 
                            isTodayStr(dateInfo.dateStr) && "bg-primary/5"
                          )}
                          title={`Column: ${dateInfo.dateStr} (${dateInfo.dayName} ${dateInfo.dayNum}) - ${sortedShifts.length} shifts`}
                        >
                          {sortedShifts.map((shift, shiftIdx) => {
                            const isSelected = selectedShifts.has(shift.id);
                            const workerColor = getWorkerColor(shift.worker_id);
                            
                            return (
                              <div
                                key={shift.id}
                                className={cn(
                                  "p-2 rounded-lg border-2 cursor-pointer transition-all text-xs relative",
                                  workerColor.bg,
                                  workerColor.border,
                                  isSelected && "ring-2 ring-primary shadow-md",
                                  "hover:shadow-md hover:scale-[1.02]"
                                )}
                                onClick={() => handleShiftClick(shift)}
                                data-testid={`shift-${shift.id}`}
                              >
                                {/* Worker Name - Prominent */}
                                <div className={cn("font-semibold text-sm truncate mb-1", workerColor.text)}>
                                  {shift.worker_name || <span className="text-amber-700 dark:text-amber-400">Open Shift</span>}
                                </div>
                                
                                {/* Time */}
                                <div className="text-muted-foreground flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {formatTimeDisplay(shift.start_time)} - {formatTimeDisplay(shift.end_time)}
                                </div>
                                
                                {/* Shift Type */}
                                <div className="text-[10px] text-muted-foreground mt-1">
                                  {shift.shift_type === 'AM' ? 'Day Shift' : shift.shift_type}
                                  {isOvernightShift(shift) && <Moon className="h-3 w-3 inline ml-1 text-indigo-500" />}
                                </div>
                                
                                {/* Status indicator */}
                                {shift.compliance_warnings?.length > 0 && (
                                  <AlertTriangle className="absolute top-1 right-1 h-3 w-3 text-amber-500" />
                                )}
                              </div>
                            );
                          })}

                          {/* Empty state for add */}
                          {dayShifts.length === 0 && canEdit && (
                            <button
                              onClick={() => {
                                setNewShift(prev => ({
                                  ...prev,
                                  house_id: house.id,
                                  start_time: `${dateInfo.dateStr}T09:00`,
                                  end_time: `${dateInfo.dateStr}T17:00`
                                }));
                                setCreateDialogOpen(true);
                              }}
                              className="w-full h-full min-h-[80px] border-2 border-dashed border-border/30 rounded-md hover:border-primary/50 hover:bg-primary/5 transition-colors flex items-center justify-center"
                            >
                              <Plus className="h-4 w-4 text-muted-foreground" />
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Shift Detail Dialog */}
      <Dialog open={shiftDialogOpen} onOpenChange={setShiftDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {isEditing ? 'Edit Shift' : 'Shift Details'}
              {selectedShift && (
                <Badge variant="outline">{selectedShift.shift_type}</Badge>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedShift && (
            <div className="space-y-4 py-4">
              {isEditing ? (
                <>
                  <div className="space-y-2">
                    <Label>House</Label>
                    <Select value={editedShift?.house_id} onValueChange={(v) => setEditedShift({...editedShift, house_id: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {houses.map(h => <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Shift Type</Label>
                    <Select value={editedShift?.shift_type} onValueChange={(v) => setEditedShift({...editedShift, shift_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="AM">AM (Day)</SelectItem>
                        <SelectItem value="PM">PM (Evening)</SelectItem>
                        <SelectItem value="Active Night">Active Night</SelectItem>
                        <SelectItem value="Sleepover">Sleepover</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start</Label>
                      <Input type="datetime-local" value={editedShift?.start_time} onChange={(e) => {
                        const newStartTime = e.target.value;
                        setEditedShift(prev => {
                          const newState = { ...prev, start_time: newStartTime };
                          if (newStartTime && prev?.end_time) {
                            const detectedType = detectShiftType(newStartTime, prev.end_time);
                            if (detectedType) newState.shift_type = detectedType;
                          }
                          return newState;
                        });
                      }} />
                    </div>
                    <div className="space-y-2">
                      <Label>End</Label>
                      <Input type="datetime-local" value={editedShift?.end_time} onChange={(e) => {
                        const newEndTime = e.target.value;
                        setEditedShift(prev => {
                          const newState = { ...prev, end_time: newEndTime };
                          if (prev?.start_time && newEndTime) {
                            const detectedType = detectShiftType(prev.start_time, newEndTime);
                            if (detectedType) newState.shift_type = detectedType;
                          }
                          return newState;
                        });
                      }} />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Auto-detects per SCHADS Award
                  </p>
                  <div className="space-y-2">
                    <Label>Worker</Label>
                    <Select value={editedShift?.worker_id || 'unassigned'} onValueChange={(v) => setEditedShift({...editedShift, worker_id: v === 'unassigned' ? null : v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {workers.map(w => <SelectItem key={w.id} value={w.id}>{w.first_name} {w.last_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={editedShift?.status} onValueChange={(v) => setEditedShift({...editedShift, status: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="offered">Offered</SelectItem>
                        <SelectItem value="accepted">Accepted</SelectItem>
                        <SelectItem value="declined">Declined</SelectItem>
                        <SelectItem value="open">Open</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea value={editedShift?.notes || ''} onChange={(e) => setEditedShift({...editedShift, notes: e.target.value})} rows={2} />
                  </div>
                </>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">House</Label>
                      <p className="font-medium">{selectedShift.house_name}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Worker</Label>
                      <p className="font-medium">{selectedShift.worker_name || 'Unassigned'}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Date</Label>
                      <p className="font-medium">{extractDateString(selectedShift.start_time)}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Time</Label>
                      <p className="font-medium">{formatTimeDisplay(selectedShift.start_time)} - {formatTimeDisplay(selectedShift.end_time)}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Status</Label>
                      <StatusBadge status={selectedShift.status} />
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Type</Label>
                      <Badge>{selectedShift.shift_type}</Badge>
                    </div>
                  </div>
                  {selectedShift.notes && (
                    <div>
                      <Label className="text-muted-foreground">Notes</Label>
                      <p className="text-sm">{selectedShift.notes}</p>
                    </div>
                  )}
                  {selectedShift.compliance_warnings?.length > 0 && (
                    <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                      <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 font-medium mb-2">
                        <AlertTriangle className="h-4 w-4" />
                        Compliance Warnings
                      </div>
                      <ul className="text-sm space-y-1">
                        {selectedShift.compliance_warnings.map((w, i) => (
                          <li key={i}>• {w}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {isEditing ? (
              <>
                <Button variant="outline" onClick={() => setIsEditing(false)}>Cancel</Button>
                <Button onClick={handleUpdateShift}>Save Changes</Button>
              </>
            ) : (
              <>
                {canEdit && (
                  <>
                    <Button variant="outline" onClick={() => setIsEditing(true)}>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    <Button variant="destructive" onClick={() => handleDeleteShift(selectedShift.id)}>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </Button>
                  </>
                )}
                <Button variant="ghost" onClick={() => setShiftDialogOpen(false)}>Close</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete {selectedShifts.size} Shifts?</DialogTitle>
            <DialogDescription>This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkDeleteDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleBulkDelete}>Delete All</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Copy Mode Dialog */}
      <Dialog open={copyDialogOpen} onOpenChange={setCopyDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5" />
              Copy Shifts
            </DialogTitle>
            <DialogDescription>Choose how you want to copy shifts</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <Button 
                variant={copyMode === 'single' ? 'default' : 'outline'} 
                className="h-auto py-4 flex flex-col gap-1"
                onClick={() => setCopyMode('single')}
              >
                <span className="font-semibold">Single</span>
                <span className="text-xs opacity-70">Copy 1 selected shift</span>
              </Button>
              <Button 
                variant={copyMode === 'bulk' ? 'default' : 'outline'} 
                className="h-auto py-4 flex flex-col gap-1"
                onClick={() => setCopyMode('bulk')}
              >
                <span className="font-semibold">Bulk Single</span>
                <span className="text-xs opacity-70">Copy {selectedShifts.size} selected</span>
              </Button>
              <Button 
                variant={copyMode === 'weekly' ? 'default' : 'outline'} 
                className="h-auto py-4 flex flex-col gap-1"
                onClick={() => setCopyMode('weekly')}
              >
                <span className="font-semibold">Weekly</span>
                <span className="text-xs opacity-70">Copy all shifts for 1 week</span>
              </Button>
              <Button 
                variant={copyMode === 'fortnightly' ? 'default' : 'outline'} 
                className="h-auto py-4 flex flex-col gap-1"
                onClick={() => setCopyMode('fortnightly')}
              >
                <span className="font-semibold">Fortnightly</span>
                <span className="text-xs opacity-70">Copy all 14 days</span>
              </Button>
            </div>
            
            {(copyMode === 'weekly' || copyMode === 'fortnightly') && (
              <div className="space-y-2">
                <Label className="text-sm">Filter by House</Label>
                <Select value={copyFilterHouse} onValueChange={setCopyFilterHouse}>
                  <SelectTrigger data-testid="copy-house-filter">
                    <SelectValue placeholder="All Houses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Houses</SelectItem>
                    {houses.map(h => (
                      <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="bg-muted/50 rounded-lg p-3 text-sm">
              {copyMode === 'single' && (
                <p>Will copy <strong>1 shift</strong> from your selection. Select exactly one shift first.</p>
              )}
              {copyMode === 'bulk' && (
                <p>Will copy <strong>{selectedShifts.size} shift(s)</strong> that you've selected to a new date.</p>
              )}
              {copyMode === 'weekly' && (
                <p>Will copy <strong>{copyFilterHouse !== 'all' ? houses.find(h => h.id === copyFilterHouse)?.name + ' shifts' : 'all shifts'} from {calendarDates[0]?.dayName} {calendarDates[0]?.dayNum} to {calendarDates[6]?.dayName} {calendarDates[6]?.dayNum}</strong> (first week of view).</p>
              )}
              {copyMode === 'fortnightly' && (
                <p>Will copy <strong>{copyFilterHouse !== 'all' ? houses.find(h => h.id === copyFilterHouse)?.name + ' shifts' : 'all shifts'} from {calendarDates[0]?.dayName} {calendarDates[0]?.dayNum} to {calendarDates[13]?.dayName} {calendarDates[13]?.dayNum}</strong> (entire fortnight).</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCopyDialogOpen(false)}>Cancel</Button>
            <Button onClick={executeCopy} disabled={(copyMode === 'single' || copyMode === 'bulk') && selectedShifts.size === 0}>
              <Copy className="h-4 w-4 mr-2" />
              Copy to Clipboard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Paste Dialog */}
      <Dialog open={pasteDialogOpen} onOpenChange={setPasteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardPaste className="h-5 w-5" />
              Paste {clipboard.length} Shift(s)
            </DialogTitle>
            <DialogDescription>
              {clipboard[0]?._copyMode === 'weekly' && 'Pasting 1 week of shifts'}
              {clipboard[0]?._copyMode === 'fortnightly' && 'Pasting 2 weeks of shifts'}
              {(clipboard[0]?._copyMode === 'single' || clipboard[0]?._copyMode === 'bulk') && `Pasting ${clipboard.length} individual shift(s)`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Target Start Date *</Label>
              <Input 
                type="date" 
                value={pasteOptions.target_date} 
                onChange={(e) => setPasteOptions({...pasteOptions, target_date: e.target.value})} 
              />
              <p className="text-xs text-muted-foreground">
                {clipboard[0]?._copyMode === 'weekly' && 'Shifts will be pasted starting from this date for 7 days'}
                {clipboard[0]?._copyMode === 'fortnightly' && 'Shifts will be pasted starting from this date for 14 days'}
                {(clipboard[0]?._copyMode === 'single' || clipboard[0]?._copyMode === 'bulk') && 'All shifts will be pasted on this date'}
              </p>
            </div>
            
            <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-1">
              <p className="font-medium">Preview:</p>
              <p>• {clipboard.length} shift(s) will be created</p>
              <p>• All shifts will keep their original times, workers, and houses</p>
              <p>• New shifts will be set to "Offered" status</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handlePasteShifts} disabled={pasteLoading || !pasteOptions.target_date}>
              {pasteLoading ? 'Creating Shifts...' : `Paste ${clipboard.length} Shift(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RosterPage;
